import { JSX, useMemo } from 'react';
import { Box } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { IClaritySubmission, IClaritySubmissionBiomaterial, type IBiomaterial } from '@zero-dash/types';
import dayjs from 'dayjs';
import CustomDataGrid from '@/components/Common/DataGrid';
import CustomChip from '@/components/Common/Chip';
import CustomTypography from '@/components/Common/Typography';
import { CustomAvatar } from '@/components/Common/Avatar';
import { chipColours, corePalette } from '@/themes/colours';
import { useUserList } from '@/hooks/useUserList';
import { AssaysOrderedCell } from '@/components/PatientDashboard/TimelineTab/BiomaterialsTable/AssaysOrderedCell';

interface IProps {
  submission: IClaritySubmission;
  biomaterials: IBiomaterial[];
}

type SubmissionRow = IClaritySubmissionBiomaterial & {
  id: number;
  name: string;
  assaysOrdered: IClaritySubmissionBiomaterial['assays'];
};

const columns: GridColDef<SubmissionRow>[] = [
  {
    field: 'name',
    headerName: 'Biomaterial Name',
    flex: 1,
    minWidth: 130,
  },
  {
    field: 'biomaterialId',
    headerName: 'Biomaterial ID',
    flex: 1,
    minWidth: 130,
  },
  {
    field: 'assays',
    headerName: 'Assays',
    flex: 2,
    minWidth: 200,
    renderCell: ({ row }) => (
      <AssaysOrderedCell
        row={{
          ...row,
          id: row.biomaterialId,
          name: String(row.biomaterialId),
          assaysOrdered: row.assays,
        }}
        dataToSubmit={[]}
      />
    ),
  },
  {
    field: 'status',
    headerName: 'Status',
    flex: 1,
    minWidth: 110,
    renderCell: ({ row }) => (
      <CustomChip
        label={row.status.toUpperCase()}
        colour={row.status === 'success' ? chipColours.chipGreenText : chipColours.chipRedText}
        backgroundColour={row.status === 'success' ? chipColours.chipGreenBg : chipColours.chipRedBg}
      />
    ),
  },
  {
    field: 'errorMessage',
    headerName: 'Error',
    flex: 2,
    minWidth: 200,
  },
];

export default function ClaritySubmissionBlock({
  submission,
  biomaterials,
}: IProps): JSX.Element {
  const { users } = useUserList();

  const submittedByUser = useMemo(
    () => users.find((u) => u.id === submission.createdBy),
    [users, submission.createdBy],
  );

  const rows = useMemo<SubmissionRow[]>(
    () => submission.items.map((item) => ({
      ...item,
      id: item.id,
      name: biomaterials.find((b) => b.id === item.biomaterialId)?.name ?? '',
      assaysOrdered: item.assays,
    })),
    [submission.items, biomaterials],
  );

  const fullName = submittedByUser
    ? `${submittedByUser.givenName} ${submittedByUser.familyName}`
    : submission.createdBy;

  return (
    <Box
      bgcolor={corePalette.white}
      border={`1px solid ${corePalette.grey50}`}
      borderRadius="8px"
      height="fit-content"
      p="16px"
      gap="8px"
      display="flex"
      flexDirection="column"
    >
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
      >
        <CustomTypography variant="bodyRegular" fontWeight="bold">
          {submission.name}
        </CustomTypography>
        <Box display="flex" alignItems="center" gap="8px">
          <CustomTypography variant="label">
            Submitted by:
          </CustomTypography>
          <CustomAvatar user={submittedByUser} size="small" />
          <CustomTypography variant="bodySmall" fontWeight="medium">
            {` ${fullName}`}
          </CustomTypography>
          <CustomTypography variant="bodySmall" color={corePalette.grey200}>
            {` ${dayjs(submission.createdAt).format('DD/MM/YYYY [at] HH:mm a')}`}
          </CustomTypography>
        </Box>
      </Box>
      <CustomDataGrid
        columns={columns}
        rows={rows}
        hideFooter
        disableColumnMenu
        disableRowSelectionOnClick
        getRowHeight={() => 'auto'}
        hideCountSummary
        sx={{
          '& .MuiDataGrid-cell': {
            display: 'flex',
            alignItems: 'center',
            py: '6px',
          },
        }}
      />
    </Box>
  );
}
